<template>
    <div id="dropdown-container" class="position-relative" ref="dropdown">
        <AutocompletInput
            :originalDomains="data.originalDomains"
            :originalRegions="data.originalRegions"
            :originalStations="data.originalStations"
            :contents="props.contents"
            :store="store"
            :state="state"
        />
        <div
            :class="[state.isDropdownOpen ? '' : 'd-none']"
            :style="{ width: props.dropdownWidth ? props.dropdownWidth : '100%!important' }"
            class="dropdown-menu autocomplete-dropdown show"
            style="max-height: 50vh; overflow-y: scroll; top: 72px !important"
        >
            <TopStations
                v-if="state.showTopStations"
                :customTopStations="props.customTopStations"
                :state="state"
                :locale="props.locale"
                :contents="props.contents"
                :se="se"
                :store="store"
            />
            <template v-else>
                <Regions :state="state" :se="se" :store="store" :contents="props.contents" />
                <Domains :state="state" :se="se" :store="store" :contents="props.contents" />
                <Stations :state="state" :se="se" :store="store" :contents="props.contents" />
                <Accommodations v-if="!store.isCosog" :state="state" :accommodations="state.accommodations" :store="store" :contents="props.contents" />
                <template v-if="noResults">
                    <div class="body text-grey-900 px-3">{{ contents.placeHolders.noResults }} - {{ state.inputValue }}</div>
                </template>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
// Vue
import { onMounted, reactive, ComputedRef, computed, ref, Ref } from 'vue'

import { onClickOutside } from '@vueuse/core'

// Template refs
const dropdown: Ref<HTMLDivElement | null> = ref(null)

// Computeds
import stationsResults from '../../../computed/stationsResults'
import regionsResults from '../../../computed/regionsResults'
import domainsResults from '../../../computed/domainsResults'
import accommodationsResults from '../../../computed/accommodationsResults'

// Classes
import { DomainData } from '../../../classes/DomainData'
import { RegionData } from '../../../classes/RegionData'
import { StationData } from '../../../classes/StationData'

// Types
import { IConstructedRegionCountry } from '../../../classes/RegionData'
import { IDomain } from '../../../classes/DomainData'
import { ICountry } from '../../../classes/StationData'

// Components
import AutocompletInput from './autocomplete-input.vue'
import TopStations from './top-stations.vue'
import Regions from './regions.vue'
import Domains from './domains.vue'
import Stations from './stations.vue'
import Accommodations from './accommodations.vue'

// State
import { AutocompleteState, getAutocompleteState , autoCompleteStateId} from '../../../state/autocompleteState'

// Props
const props = defineProps<{
    locale: string
    se: any
    store: any
    contents: any
    dropdownWidth?: string
    customTopStations?: number[]
}>()

interface IData {
    originalRegions: IConstructedRegionCountry[]
    originalStations: ICountry[]
    originalDomains: IDomain[]
}

const data: IData = reactive({
    originalDomains: [],
    originalStations: [],
    originalRegions: []
})


// Computed
const state: ComputedRef<AutocompleteState> = computed(() => {
    const { state, id } = getAutocompleteState(autoCompleteStateId.value)
    return state
})

const noResults: ComputedRef<boolean> = computed(() => {
    const result: boolean =
        !stationsResults.value &&
        !regionsResults.value &&
        !domainsResults.value &&
        !accommodationsResults.value &&
        state.value.madeAccommodationsRequest
    return result
})

const domains = computed(() => {
    const domains = props.store.data.geoData.domains.filter((domain: any) => {
        return props.store.data.available.domainsMap[domain.id]
    })

    return domains
})

const regions = computed(() => {
    const regions = props.store.data.geoData.regions.filter((region: any) => {
        return props.store.data.available.regionsMap[region.id]
    })

    return regions
})

const stations = computed(() => {
    const stations = props.store.data.geoData.stations.filter((station: any) => {
        return props.store.data.available.stationsMap[station.id]
    })

    return stations
})

const parentStations = computed(() => {
    const parentStations = props.store.data.geoData.parentStations.filter((station: any) => {
        return props.store.data.available.parentStationsMap[station.id]
    })

    return parentStations
})

// Methods
const init = () => {
    props.se.onAvailableFieldValues(
        'region',
        () => {
            data.originalRegions = new RegionData(regions.value, props.store.data.geoData.countries).constructedRegionCountries
            state.value.regions = data.originalRegions
        },
        true
    )
    props.se.onAvailableFieldValues(
        'station',
        () => {
            data.originalStations = new StationData(
                stations.value,
                parentStations.value,
                props.store.data.geoData.countries
            ).constructedStationCountries
            state.value.stations! = data.originalStations
        },
        true
    )

    if(props.store.state.summer) return

    props.se.onAvailableFieldValues(
        'domaineSki',
        () => {
            data.originalDomains = new DomainData(domains.value).constructedDomains
            state.value.domains = data.originalDomains
        },
        true
    )

}

// Lifycycle
onMounted(async () => {
    autoCompleteStateId.value = props.store.id

    init()

    const inputOverlay: HTMLDivElement | null = document.querySelector('#input-overlay')
    onClickOutside(dropdown.value, () => {
        state.value.isDropdownOpen = false

        if (inputOverlay) {
            inputOverlay.style.display = 'flex'
        }
    })
})
</script>

<style>
.autocomplete-dropdown .item:hover {
    background-color: var(--blue-100);
}

.autocomplete-dropdown .selected-item:hover {
    background-color: var(--primary-blue);
}

.selected-item {
    color: var(--primary-white) !important;
}

.white-x::before,
.white-x::after {
    background-color: var(--primary-white) !important;
}

.close-container {
    border: 1px solid;
    border-radius: 50%;
}
</style>
